import type { ReactNode } from 'react';
import { Skeleton, EmptyState } from '@xcentral/ui';
import { useClientTiers } from './ClientTiersProvider';

/**
 * #12629 (CPT-2) — route guard for tier-gated routes. Reads the caller's tier from
 * GET /api/v1/client-it/me (via useClientTiers), not the ID token.
 *
 * #20070 (F11.5 foundation C) adds `anyOfTiers`: the caller passes if they hold ANY of the listed
 * tiers — "My requests" is ClientAdmin or ClientUser and never ClientBilling, which one
 * `requiredTier` cannot express. Existing `requiredTier` callers are unchanged. Pass exactly one of
 * the two; with both, the caller must satisfy both. The guard is presentation — the controllers'
 * audiences are the enforcement.
 */
type TierGuardProps = {
  children: ReactNode;
} & (
  | { requiredTier: string; anyOfTiers?: readonly string[] }
  | { requiredTier?: undefined; anyOfTiers: readonly string[] }
);

/** Does the caller satisfy a tier requirement? Shared with the nav filter so the two cannot drift. */
export function satisfiesTiers(
  hasTier: (t: string) => boolean,
  requiredTier?: string,
  anyOfTiers?: readonly string[],
): boolean {
  if (requiredTier && !hasTier(requiredTier)) return false;
  if (anyOfTiers && !anyOfTiers.some((t) => hasTier(t))) return false;
  return true;
}

export function TierGuard({ requiredTier, anyOfTiers, children }: TierGuardProps) {
  const { loading, hasTier } = useClientTiers();

  if (loading) return <Skeleton lines={6} />;

  if (!satisfiesTiers(hasTier, requiredTier, anyOfTiers)) {
    return (
      <EmptyState
        title="Access restricted"
        description={
          requiredTier === 'ClientAdmin'
            ? "This area is for Client Administrators. If you need access, ask your account's Client Administrator or contact Conscierra."
            : "Your account role doesn't include this area. If you need access, ask your account's Client Administrator or contact Conscierra."
        }
      />
    );
  }

  return <>{children}</>;
}
